<template>
  <div class="relative w-full h-full" ref="roomMapContainer">
    <svg
      class="overflow-visible"
      width="100%"
      height="100%"
      :viewBox="`0 0 ${svgRelativeWidth} ${svgRelativeHeight}`"
      preserveAspectRatio="xMidYMin meet"
      ref="svgElementRef"
      :role="role"
      :aria-label="ariaLabel"
      :aria-describedby="ariaDescribedby"
    >
      <RoomGrid
        v-if="displayGrid"
        :columnCount="svgRelativeWidth"
        :svgHeight="svgRelativeHeight"
        :ratio="svgScale"
      />

      <!-- Render this doing our own math to force Safari to render it properly  -->
      <image
        ref="venueBackgroundImage"
        id="room-background-image"
        v-if="backgroundImagePath"
        :href="backgroundImagePath"
        role="img"
        :aria-label="resolvedBackgroundImageAriaLabel"
        x="0"
        y="0"
        :width="svgDOMWidth"
        :height="svgDOMHeight"
        :transform="`scale(${1 / svgScale})`"
      />

      <g id="room-table-group" role="group" :aria-label="tableGroupAriaLabel">
        <slot />
      </g>
    </svg>
  </div>
</template>

<script lang="ts" setup>
import { useColorMode, useElementBounding, useResizeObserver } from '@vueuse/core'
import { computed, onMounted, provide, readonly, ref, toRefs, watch } from 'vue'

import RoomGrid from '@/common/components/atoms/RoomGrid.vue'
import { ROOM_MAP_BG_MODE, RoomMapBgMode } from '@/common/interfaces/RoomMap'
import { RovingTabindex } from '@/common/utils/RovingTabindex'
import { useSettingStore } from '@/stores/setting'
import { ROOM_TABLE_RELATIVE_WIDTH } from '@/utils/utils'

import { RoomLayoutRead } from '@generated/types'

const props = withDefaults(
  defineProps<{
    displayGrid?: boolean
    backgroundMode?: RoomMapBgMode
    roomLayout?: RoomLayoutRead
    ariaLabel?: string
    ariaDescribedby?: string
    role?: string
    backgroundImageAriaLabel?: string
    tableGroupAriaLabel?: string
  }>(),
  {
    displayGrid: false,
    backgroundMode: ROOM_MAP_BG_MODE.USER_CHOICE,
    roomLayout: undefined,
    ariaLabel: undefined,
    ariaDescribedby: undefined,
    role: 'radiogroup',
    backgroundImageAriaLabel: undefined,
    tableGroupAriaLabel: undefined,
  },
)
const {
  displayGrid,
  backgroundMode,
  roomLayout,
  ariaLabel,
  ariaDescribedby,
  backgroundImageAriaLabel,
} = toRefs(props)
const resolvedBackgroundImageAriaLabel = computed<string>(
  () => backgroundImageAriaLabel.value || `${ariaLabel.value || 'Venue'} background layout`,
)
const settingStore = useSettingStore()
const aspectRatio = computed<number>(() => roomLayout.value?.aspect_ratio || settingStore.ratio)
const lastRoomLayout = ref<RoomLayoutRead | undefined>(roomLayout.value)
watch(roomLayout, layout => {
  if (layout) lastRoomLayout.value = layout
})

const background_path = computed<string>(
  () => lastRoomLayout.value?.background_url || settingStore.background_path,
)
const dark_background_path = computed<string>(
  () => lastRoomLayout.value?.dark_background_url || settingStore.dark_background_path,
)

const colorMode = useColorMode()
const backgroundImagePath = computed<string>(() => {
  if (backgroundMode.value === ROOM_MAP_BG_MODE.USER_CHOICE) {
    return colorMode.value === 'dark' ? dark_background_path.value : background_path.value
  } else if (backgroundMode.value === ROOM_MAP_BG_MODE.FORCE_DARK) {
    return dark_background_path.value
  } else {
    return background_path.value
  }
})

const svgElementRef = ref<SVGElement | null>(null)
onMounted(() => {
  if (!svgElementRef.value) return
  new RovingTabindex(svgElementRef.value, { mode: 'focus-only' })
})
provide('svgElementRef', readonly(svgElementRef))
const svgDOMWidth = ref<number>(500)
const svgDOMHeight = computed<number>(() => svgDOMWidth.value / aspectRatio.value)
const svgRelativeWidth = computed<number>(() => {
  if (!roomLayout.value) return ROOM_TABLE_RELATIVE_WIDTH
  return roomLayout.value.svg_height * roomLayout.value.aspect_ratio
})
const svgRelativeHeight = computed(() => {
  if (!roomLayout.value) return svgRelativeWidth.value / aspectRatio.value
  return roomLayout.value.svg_height
})
const svgScale = computed(() => svgDOMWidth.value / svgRelativeWidth.value)

// The background image represents the actual size of the venue room
// The svgDOMWidth and svgDOMHeight values don't represent the size of the venue room
// because the SVG is resized to fit its parent container
// as a result, the venue image is scaled down to fit centered within the SVG
const venueBackgroundImage = ref<HTMLImageElement | null>(null)
const { width: venueDOMWidth } = useElementBounding(venueBackgroundImage)
// Padding in X axis from the border of the SVG to the border of the venue room
const paddingX = computed(() => (svgDOMWidth.value - venueDOMWidth.value) / 2)
provide('paddingX', readonly(paddingX))
const venueScale = computed<number>(() => venueDOMWidth.value / ROOM_TABLE_RELATIVE_WIDTH)
provide('venueScale', readonly(venueScale))
provide('svgRelativeWidth', readonly(svgRelativeWidth))
provide('svgRelativeHeight', readonly(svgRelativeHeight))

const roomMapContainer = ref<HTMLDivElement | null>(null)
useResizeObserver(roomMapContainer, ([entry]) => {
  svgDOMWidth.value = entry.contentRect.width
})
</script>
